/**
 * Multi Set entity class
 */

import { ascBy, has, lowerCaseProperty } from '../../../app/helpers/object'
import { dateAsUTC } from '../../../app/helpers/date'

/**
 * MultiSetFactory
 * returns MultiSet entity class
 *
 * @class MultiSetFactory
 * @memberof mwApp
 * @see mwApp
 * @return {Function} {@link models.MultiSet} class
 *
 * @ngdoc factory
 * @name mwApp.factory:MultiSetFactory
 */
/* @ngInject */
export default function MultiSetFactory ($http, $injector, Entity, FileUploadMixin, alertLiteSvc, onOffMixin) {
  /* === MultiSet class === */

  /**
   * MultiSet model
   *
   * @class MultiSet
   * @classdesc MultiSet model
   * @memberof models
   * @see models
   * @extends models.Entity
   * @mixes models.onOffMixin
   * @mixes models.FileUploadMixin
   * @return {models.MultiSet} MultiSet entity
   */
  function MultiSet () {
    const obj = Entity.apply(this, arguments)
    if (obj) {
      return obj
    }
  }
  MultiSet.prototype = Object.create(Entity.prototype)
  MultiSet.prototype.constructor = MultiSet

  /* === MultiSet instance methods & fields === */

  Object.assign(MultiSet.prototype, {

    /**
     * instance fields
     *
     * @memberof models.MultiSet#
     * @type {Object}
     */
    _fields: {
      id: Number,
      networkId: Number,
      name: String,
      quantity: Number,
      enabled: true,
      exclusivePaymentSystemSetting: {
        id: Number,
        assignedPaymentSystems: [{ paymentSystemId: Number, paymentSystemType: String }]
      },
      updatedAt: String,
      updatedBy: String,
      coverImageUrl: String,
      description: String,
      richDescription: String,
      assignedSubscriptions: [{ id: Number, name: String, paid: Boolean }],
      type: String,
      pricing: {
        id: Number,
        currency: 'Currency',
        rent: {
          amount: Number,
          autoPay: null,
          dueDate: String,
          period: Number,
          periodUnlimited: Boolean,
          rentLengthOverride: Boolean(false),
          timeUnit: String,
          $periodType: 'DEFAULT'
        }
      },
      channelSets: [{
        id: Number,
        name: String
      }],
      liveEventSets: [{
        id: Number,
        name: String
      }],
      tvShowSets: [{
        id: Number,
        name: String
      }],
      vodSets: [{
        id: Number,
        name: String
      }],
      $rentEnabled: true,
      numberOfCcu: Number,
      $overrideCCU: Boolean
    },

    /**
     * url template
     *
     * @memberof models.MultiSet#
     * @type {String}
     */
    _urlTemplate: 'api/networks/{{::networkId}}/multi-sets/{{::id}}',

    /**
     * url template for available channel sets
     *
     * @memberof models.MultiSet#
     * @type {String}
     */
    _availableMultiSetsTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/channelsetpricings/extra/multi-sets',

    /**
     * public url template
     *
     * @memberof models.MultiSet#
     * @type {String}
     */
    _publicUrlTemplate: 'organizations/{{::networkId}}/contentSets/multi/{{::id}}',

    /**
     * url template for loading content sets
     *
     * @memberof models.channelSet#
     * @type {String}
     */
    _subscriptionUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/multi-sets',

    /**
     * url template for checking set intersection
     *
     * @memberof models.channelSet#
     * @type {String}
     */
    _contentIntersectionUrlTemplate: 'api/networks/{{::networkId}}/multisets/{{::id}}/intersection',

    /**
     * url template for loading custom prices
     *
     * @memberof models.channelSet#
     * @type {String}
     */
    _itemsUrlTemplate: 'api/networks/{{::networkId}}/subscriptions/{{::subscriptionId}}/multi-sets/{{::setId}}/content',

    /**
     * Load channels extra for Channel set
     *
     * @memberof models.MultiSet#
     * @returns {Promise} promise
     */
    _getAvailableItems: function (networkId, type, params = {}) {
      const extraUrl = `api/networks/${networkId}/multi-sets/extra/${type}?sortBy=name`
      return $http.get(extraUrl, { params })
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },

    _checkForUsageInDiscounts: function () {
      return $http.get(this._url + '/usage')
        .data()
        .then((response) => response.payload)
        .catch(rejection => alertLiteSvc.alertError(rejection))
    },

    /**
     * Prepare object
     *
     * @memberof models.MultiSet#
     * @override
     */
    _prepare: function () {
      this.$rentEnabled = true
      if (!this.pricing) {
        this.pricing = {
          rent: {
            paymentMethod: 'PER_SET',
            amount: 0,
            period: 1,
            timeUnit: 'DAY'
          }
        }
      }
      if (this.pricing.rent.dueDate) {
        this.pricing.rent.$periodType = 'DUE_DATE'
        this.pricing.rent.$dueDate = new Date(this.pricing.rent.dueDate)
      } else {
        this.pricing.rent.$periodType = 'DEFAULT'
      }

      if (this.channels) {
        this.channels.sort(ascBy(lowerCaseProperty('name')))
      }
    },

    /**
     * Get underlying object
     *
     * @memberof models.channelSet#
     * @return {Object} new underlying object as plain object
     * @override
     */
    _object: function () {
      if (this.pricing?.rent) {
        switch (this.pricing.rent.$periodType) {
          case 'DUE_DATE':
            this.pricing.rent.dueDate = dateAsUTC(this.pricing.rent.$dueDate, 'YYYY-MM-DD')
            this.pricing.rent.periodUnlimited = false
            this.pricing.rent.period = 0
            this.pricing.rent.timeUnit = null
            this.pricing.rent.autoPay = false
            break
          case 'DEFAULT':
            this.pricing.rent.dueDate = null
            break
          default:
            this.pricing.rent.dueDate = null
            break
        }
      }
      return Entity.prototype._object.apply(this, arguments)
    }
  }, FileUploadMixin, onOffMixin)

  /* === MultiSet static methods & fields === */

  /**
   * Generate url
   *
   * @param uri
   * @param {Object} [o] - object over which Url will be interpolated
   * @return {String}
   */
  MultiSet.generateUrl = function (uri = '', o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = cast.prototype.$_urlTemplate(o)
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url
    return url + uri
  }

  /**
   * Get assigned shows
   *
   * @memberof models.MultiSet#
   */
  MultiSet.getAssignedChannels = function (tableState, networkId, csId = null) {
    const params = {
      page: tableState.page || 0,
      count: 25
    }

    if (Object.prototype.hasOwnProperty.call(tableState, 'q')) {
      params.q = tableState.q
    }

    let url = `/nora/api/networks/${networkId}/multisets/extra/channels/assigned`
    if (csId) {
      url = `/nora/api/networks/${networkId}/multisets/${csId}/extra/channels/assigned`
    }

    return $http.get(url, { params })
      .data()
      .then((response) => response.payload)
      .catch(rejection => alertLiteSvc.alertError(rejection))
  }

  /**
   * Get channel sets by params
   *
   * @memberof models.MultiSet#
   */
  MultiSet.loadSmartMultiSets = function (tableState, o) {
    o = o || {}

    const url = MultiSet.generateUrl('', o)

    const params = {
      'sort-by': tableState.sort.predicate ? tableState.sort.predicate : '',
      'sort-order': tableState.sort.reverse === false ? 'asc' : 'desc',
      page: Math.floor(tableState.pagination.start / tableState.pagination.number) || 0,
      count: tableState.pagination.number
    }
    Object.assign(params, tableState.search.predicateObject)
    if (params.$) {
      params.q = params.$
      delete params.$
    }
    if (params.updatedBy) {
      params['updated-by-like'] = params.updatedBy
      delete params.updatedBy
    }
    if (params.name) {
      params['name-like'] = params.name
      delete params.name
    }

    if (o.searchChannel) {
      params.channel = o.searchChannel.id
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          const result = this.cast(data, {
            // response structure
            content: [this._name],
            first: true,
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            sort: [{
              ascending: true,
              direction: '',
              ignoreCase: true,
              nullHandling: '',
              property: ''
            }],
            totalElements: 0,
            totalPages: 0
          })

          // change SmartTable state
          if (Array.isArray(result.sort) && result.sort.length) {
            const last = result.sort.pop()
            tableState.sort.predicate = last.property
            tableState.sort.reverse = !last.ascending
          }
          tableState.pagination.start = result.number * result.size // first element
          tableState.pagination.number = result.size // page size
          tableState.pagination.numberOfPages = result.totalPages
          tableState.pagination.totalItemCount = result.totalElements

          // return array for SmartTable
          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  MultiSet.loadMultiSets = function (tableState, o) {
    o = o || {}

    const cast = $injector.get(this._name) // get constructor from injector by name
    const url = cast.prototype.$_availableMultiSetsTemplate(o)

    const params = {
      page: tableState.page || 0,
      count: tableState.count,
      'name-like': o.search || null
    }
    return $http.get(url, { params })
      .then(
        (response) => {
          const data = has('payload')(response.data)
            ? response.data.payload
            : response.data

          const result = this.cast(data, {
            // response structure
            content: [cast._name],
            last: true,
            number: 0,
            numberOfElements: 0,
            size: 0,
            totalElements: 0,
            totalPages: 0
          })

          tableState.page = result.number
          tableState.count = result.size // page size

          return result
        }
      ).catch(alertLiteSvc.alertError)
  }

  MultiSet.loadItemsForSubscription = function (ids) {
    const cast = $injector.get(this._name) // get constructor from injector by name
    let url = `${cast.prototype.$_itemsUrlTemplate(ids)}`
    url = url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url

    return $http.get(url)
      .then(
        (response) => {
          return has('payload')(response.data)
            ? response.data.payload
            : response.data
        }
      ).catch(alertLiteSvc.alertError)
  }

  // copy all static methods from Entity class
  Object.assign(MultiSet, Entity)
  MultiSet._name = 'MultiSet'
  MultiSet._publicName = 'Multi Set'
  MultiSet.init()

  // permissions
  MultiSet.access = {
    view: '',
    create: '',
    edit: '',
    delete: ''
  }

  return MultiSet
}
